import { useState } from 'react'
import { api, errorText } from '../api/client'
import type { ConsentBasis } from '../api/types'
import { CONSENT_BASES } from '../lib/constants'
import { Icon } from './Icon'
import { Modal } from './Modal'
import { useToast } from './Toast'

/** Record an opt-in the officer already has.
 *
 *  This is a statement about the file — "they gave us this number on the
 *  application" — not a step in sending a text, so it does not live inside the
 *  composer. It lives on the record, next to the consent it changes. The CRM never
 *  manufactures consent: the officer is attesting to something documented
 *  elsewhere, and the attestation is logged under their name. */
export function ConsentModal({ contactId, name, channel = 'sms', current, onClose, onSaved }: {
  contactId: number
  name: string
  channel?: 'sms' | 'whatsapp' | 'email'
  /** What the record says today, so the dialog opens on the likely next move. */
  current?: string | null
  onClose: () => void
  onSaved?: () => void
}) {
  // Three things an officer needs to be able to say about consent, not one: we
  // have it, they have withdrawn it, and we recorded it by mistake. Only the
  // middle one is a legal event; the last is a correction to the file.
  const [status, setStatus] = useState<'opted_in' | 'opted_out' | 'none'>(
    current === 'opted_in' ? 'opted_out' : 'opted_in')
  const [basis, setBasis] = useState<ConsentBasis>('prior_express')
  const [note, setNote] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [busy, setBusy] = useState(false)
  const toast = useToast()

  const label = channel === 'sms' ? 'texts' : channel === 'whatsapp' ? 'WhatsApp' : 'email'

  async function save() {
    setBusy(true)
    try {
      await api.recordConsent(contactId, {
        channel, status,
        basis: status === 'opted_in' ? basis : undefined,
        note: note || undefined,
      })
      toast(status === 'opted_in' ? `Opt-in recorded for ${name}`
        : status === 'opted_out' ? `${name} marked as opted out`
          : `Consent record cleared for ${name}`, { tone: 'positive' })
      onSaved?.()
      onClose()
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
      setBusy(false)
    }
  }

  return (
    <Modal open width={500} title={`Record consent · ${name}`} onClose={onClose}
      subtitle={`Only if you already hold it. This is what lets the CRM send ${label}.`}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className={`btn ${status === 'opted_in' ? 'primary' : 'danger-solid'}`}
            onClick={save} disabled={(status === 'opted_in' && !confirmed) || busy}>
            <Icon name={status === 'opted_in' ? 'verified_user' : 'block'} size={17} />
            {busy ? 'Saving…' : status === 'opted_in' ? 'Record consent'
              : status === 'opted_out' ? 'Record their opt-out' : 'Clear the record'}
          </button>
        </>
      }>
      <div className="stack">
        <div className="consent-choice">
          {([
            ['opted_in', 'They gave consent', 'We hold it and can send'],
            ['opted_out', 'They asked to stop', 'Withdrawn — nothing more goes out'],
            ['none', 'Remove the record', 'Recorded by mistake; back to no consent'],
          ] as const).map(([key, label, hint]) => (
            <button key={key} className={`consent-opt${status === key ? ' on' : ''}`}
              onClick={() => setStatus(key)}>
              <strong>{label}</strong><small>{hint}</small>
            </button>
          ))}
        </div>

        {status === 'opted_in' ? (
          <>
            <label className="fl">
              <span>How did they consent?</span>
              <select className="field" value={basis}
                onChange={(e) => setBasis(e.target.value as ConsentBasis)}>
                {CONSENT_BASES.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
              </select>
            </label>
            <label className="fl">
              <span>Note <span className="hint">— where it's documented</span></span>
              <input className="field" value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Signed application, Sep 3" />
            </label>
            <label className="checkrow">
              <input type="checkbox" checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)} />
              <span>I have this consent on record. Recording it is logged under my name.</span>
            </label>
          </>
        ) : (
          <label className="fl">
            <span>Note <span className="hint">— what happened</span></span>
            <input className="field" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Replied STOP, or recorded in error" />
          </label>
        )}
        <p className="quiet" style={{ fontSize: 11.5, margin: 0 }}>
          {status === 'opted_out'
            ? 'An opt-out is permanent until they ask for it back in writing. Nothing goes out on this channel once it is recorded.'
            : status === 'none'
              ? 'This clears what we claim to hold. Use it for a mistaken entry — if they actually asked to stop, record the opt-out instead.'
              : null}
        </p>
        <p className="quiet" style={{ fontSize: 11.5, margin: 0 }}>
          TCPA puts the burden of proof on us. If it isn’t written down somewhere you could
          produce, it isn’t consent — ask them for it instead.
        </p>
      </div>
    </Modal>
  )
}
